/**
 * 天元计算 — 应用入口
 *
 * 本文件负责：
 *  - 导入各子模块（W11–W16 逐步填充）
 *  - 连接 WebSocket（W12）
 *  - 绑定工具栏按钮事件
 */

// ── 分割条 ────────────────────────────────────────────────────────────────────
import { initResizers } from './resizer.js';

// ── W11：编辑器 ───────────────────────────────────────────────────────────────
import { createEditor, getCode, undo, redo } from './editor.js';

// ── W12：WebSocket 客户端 ─────────────────────────────────────────────────────
import { connect, execute, interrupt, clearWorkspace, getSessionId, isConnected } from './ws.js';

// ── W15：REPL 输出区 ──────────────────────────────────────────────────────────
import { appendCode, appendError, appendOutput } from './output.js';

// ── W16：工作区变量面板 ───────────────────────────────────────────────────────
import { refreshVars } from './workspace.js';

// ── W17：代码自动补全 ─────────────────────────────────────────────────────────
import { refreshVarCompletions } from './autocomplete.js';
import { reportPageView } from './pageview.js';
import { buildLoginUrl, fetchCurrentUser, logout } from './auth-state.js';
import { capabilityEnabled, fetchPublicRollout } from './rollout.js';
import { getLocale, localeHeaderValue, onLocaleChange } from './locale.js';
import { formatMessage, getMessages } from './i18n.js';
import { formatDurationSeconds } from './formatters.js';
import { formatTianyuanCode, normalizeLintResponse } from './ide-tools.js';
import { showConfirmDialog, showPromptDialog } from './dialog.js';
// ── 状态提示 ─────────────────────────────────────────────────────────────────
const statusEl    = document.getElementById('status-text');
const btnRun      = document.getElementById('btn-run');
const btnStop     = document.getElementById('btn-stop');
const authPill = document.getElementById('auth-pill');
const authPillText = document.getElementById('auth-pill-text');
const authPillLink = document.getElementById('auth-pill-link');
const quotaPill = document.getElementById('quota-pill');
const quotaLabel = document.getElementById('quota-label');
const quotaText = document.getElementById('quota-text');
const menuAccountCenter = document.getElementById('menu-account-center');
const menuAuthAction = document.getElementById('menu-auth-action');
const menuHelpBilling = document.getElementById('menu-help-billing');
const menuHelpEducation = document.getElementById('menu-help-education');
const menuHelpTeams = document.getElementById('menu-help-teams');
const projectModal = document.getElementById('project-modal');
const projectClose = document.getElementById('project-close');
const projectListBody = document.getElementById('project-list-body');
const projectRevisionsBody = document.getElementById('project-revisions-body');
const projectNameInput = document.getElementById('project-name-input');
const projectDescInput = document.getElementById('project-desc-input');
const projectVisibilitySelect = document.getElementById('project-visibility-select');
const projectSaveBtn = document.getElementById('project-save-btn');
const projectStatusEl = document.getElementById('project-status');
const btnCloudSave = document.getElementById('btn-cloud-save');
const menuFileCloudSave = document.getElementById('menu-file-cloud-save');
const messageModal = document.getElementById('message-modal');
const messageModalContent = messageModal?.querySelector('.message-modal-content');
const messageModalTitle = document.getElementById('message-modal-title');
const messageModalText = document.getElementById('message-modal-text');
const messageModalClose = document.getElementById('message-modal-close');
const messageModalConfirm = document.getElementById('message-modal-confirm');
const workspaceAuthModal = document.getElementById('workspace-auth-modal');
const workspaceAuthClose = document.getElementById('workspace-auth-close');
const workspaceAuthForm = document.getElementById('workspace-auth-form');
const workspaceAuthTitle = document.getElementById('workspace-auth-title');
const workspaceAuthSubtitle = document.getElementById('workspace-auth-subtitle');
const workspaceAuthIdentifierLabel = document.getElementById('workspace-auth-identifier-label');
const workspaceAuthPasswordLabel = document.getElementById('workspace-auth-password-label');
const workspaceAuthIdentifierInput = document.getElementById('workspace-auth-identifier');
const workspaceAuthPasswordInput = document.getElementById('workspace-auth-password');
const workspaceAuthSubmit = document.getElementById('workspace-auth-submit');
const workspaceAuthStatus = document.getElementById('workspace-auth-status');
const workspaceAuthSignup = document.getElementById('workspace-auth-signup');
const workspaceAuthForgot = document.getElementById('workspace-auth-forgot');
const workspaceAuthLater = document.getElementById('workspace-auth-later');
const feedbackModal = document.getElementById('feedback-modal');
const feedbackModalTitle = document.getElementById('feedback-modal-title');
const feedbackClose = document.getElementById('feedback-close');
const feedbackTitleLabel = document.getElementById('feedback-title-label');
const feedbackDescriptionLabel = document.getElementById('feedback-description-label');
const feedbackCodeLabel = document.getElementById('feedback-code-label');
const feedbackTitleInput = document.getElementById('feedback-title-input');
const feedbackDescriptionInput = document.getElementById('feedback-description-input');
const feedbackCodeInput = document.getElementById('feedback-code-input');
const feedbackFillCodeBtn = document.getElementById('feedback-fill-code');
const feedbackClearCodeBtn = document.getElementById('feedback-clear-code');
const feedbackSubmitBtn = document.getElementById('feedback-submit-btn');
const feedbackPageMeta = document.getElementById('feedback-page-meta');
const feedbackSessionMeta = document.getElementById('feedback-session-meta');
const menuHelpFeedback = document.getElementById('menu-help-feedback');
const btnFeedback = document.getElementById('btn-feedback');
const INTERRUPT_BUTTON_DELAY_MS = 300;
const WORKSPACE_USAGE_HEARTBEAT_MS = 10000;
let stopButtonTimer = null;
let currentUser = null;
let currentProjectId = null;
let currentProjectName = null;
let projectSaveInFlight = false;
let feedbackSubmitInFlight = false;
let messageModalTimer = null;
let messageModalDismissible = true;
let lintInFlight = false;
let workspaceConnectionStarted = false;
let workspaceAuthSubmitting = false;
let currentBilling = null;
let quotaLoadInFlight = false;
let quotaClientActiveStartedAt = null;
let quotaDisplayTimer = null;
let workspaceUsageTimer = null;
let workspaceUsageInFlight = false;
let localizedStatus = null;

function t() {
  return getMessages('pages/workspace', getLocale());
}

function setMenuLabel(id, text) {
  const node = document.getElementById(id);
  if (node?.childNodes?.[0]) {
    node.childNodes[0].nodeValue = text;
  }
}

function setAuthLoadingState() {
  if (authPill) {
    authPill.hidden = true;
  }
  if (quotaPill) {
    quotaPill.hidden = true;
  }
  if (menuAccountCenter) {
    menuAccountCenter.style.display = 'none';
  }
  if (menuAuthAction) {
    menuAuthAction.style.display = 'none';
  }
}

function setStatus(text, color = '#858585', { keepLocalized = false } = {}) {
  if (!keepLocalized) {
    localizedStatus = null;
  }
  if (statusEl) {
    statusEl.textContent = text;
    statusEl.style.color = color;
  }
}

function formatQuotaDuration(seconds) {
  const value = Math.max(0, Math.floor(Number(seconds || 0)));
  if (value <= 0) return formatDurationSeconds(0);
  const isEn = getLocale() === 'en';
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const secs = value % 60;
  if (hours > 0) {
    if (minutes <= 0) return isEn ? `${hours} hr` : `${hours} 小时`;
    return isEn ? `${hours} hr ${minutes} min` : `${hours} 小时 ${minutes} 分钟`;
  }
  if (minutes > 0) {
    if (secs <= 0) return isEn ? `${minutes} min` : `${minutes} 分钟`;
    return isEn ? `${minutes} min ${secs} sec` : `${minutes} 分钟 ${secs} 秒`;
  }
  return isEn ? `${secs} sec` : `${secs} 秒`;
}

function getQuotaClientActiveSeconds() {
  if (!quotaClientActiveStartedAt || !isWorkspaceUsageActive()) return 0;
  return Math.max(0, Math.floor((Date.now() - quotaClientActiveStartedAt) / 1000));
}

function startQuotaDisplayTicker() {
  if (quotaDisplayTimer) return;
  quotaDisplayTimer = window.setInterval(renderQuotaPill, 1000);
}

function stopQuotaDisplayTicker() {
  if (!quotaDisplayTimer) return;
  window.clearInterval(quotaDisplayTimer);
  quotaDisplayTimer = null;
}

function updateQuotaDisplayTicker() {
  if (currentUser && currentBilling && isWorkspaceUsageActive()) {
    startQuotaDisplayTicker();
  } else {
    stopQuotaDisplayTicker();
  }
}

function resetQuotaClientUsageBaseline() {
  quotaClientActiveStartedAt = isWorkspaceUsageActive() ? Date.now() : null;
  updateQuotaDisplayTicker();
}

function renderQuotaPill({ loading = false } = {}) {
  if (!quotaPill || !quotaLabel || !quotaText) return;
  quotaPill.hidden = false;
  quotaPill.classList.remove('is-low', 'is-empty');
  quotaPill.title = '';
  quotaLabel.textContent = currentBilling?.plan?.code === 'free' ? t().quotaLabelFree : t().quotaLabel;

  if (!currentUser) {
    quotaText.textContent = t().quotaLoginRequired;
    return;
  }
  if (loading && !currentBilling) {
    quotaText.textContent = t().quotaLoading;
    return;
  }
  if (!currentBilling) {
    quotaText.textContent = t().quotaUnavailable;
    return;
  }

  const totalSeconds = Number(currentBilling.capabilities?.max_daily_workspace_seconds || 0);
  const rawUsedSeconds = Number(currentBilling.quota?.workspace_active_seconds || 0) + getQuotaClientActiveSeconds();
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    quotaText.textContent = t().quotaUnlimited;
    return;
  }

  const usedSeconds = Math.min(totalSeconds, rawUsedSeconds);
  const remainingSeconds = Math.max(0, totalSeconds - usedSeconds);
  const percentRemaining = totalSeconds > 0 ? remainingSeconds / totalSeconds : 1;
  if (remainingSeconds <= 0) {
    quotaPill.classList.add('is-empty');
  } else if (percentRemaining <= 0.2) {
    quotaPill.classList.add('is-low');
  }
  quotaText.textContent = formatMessage(t().quotaRemaining, {
    remaining: formatQuotaDuration(remainingSeconds),
    total: formatQuotaDuration(totalSeconds),
    used: formatQuotaDuration(usedSeconds),
  });
  quotaPill.title = formatMessage(t().quotaTitle, {
    remaining: formatQuotaDuration(remainingSeconds),
    total: formatQuotaDuration(totalSeconds),
    used: formatQuotaDuration(usedSeconds),
  });
}

function setProjectStatus(text, color = '#64748b') {
  if (!projectStatusEl) return;
  projectStatusEl.textContent = text;
  projectStatusEl.style.color = color;
}

function hideMessageModal() {
  if (!messageModal) return;
  if (messageModalTimer) {
    clearTimeout(messageModalTimer);
    messageModalTimer = null;
  }
  messageModal.style.display = 'none';
}

function showMessageModal(text, {
  title = t().systemMessage,
  kind = 'info',
  autoCloseMs = null,
  dismissible = true,
} = {}) {
  if (!messageModal || !messageModalTitle || !messageModalText || !messageModalContent) return;
  if (messageModalTimer) {
    clearTimeout(messageModalTimer);
    messageModalTimer = null;
  }
  messageModalTitle.textContent = title;
  messageModalText.textContent = text || '';
  messageModalContent.dataset.kind = kind;
  if (messageModalClose) {
    messageModalClose.style.display = dismissible ? '' : 'none';
  }
  if (messageModalConfirm) {
    messageModalConfirm.style.display = dismissible ? 'inline-flex' : 'none';
  }
  messageModalDismissible = dismissible;
  messageModal.style.display = 'block';
  if (autoCloseMs && autoCloseMs > 0) {
    messageModalTimer = window.setTimeout(() => {
      hideMessageModal();
    }, autoCloseMs);
  }
}

function pushStatus(text, color = '#858585', options = {}) {
  setStatus(text, color);
  if (!text) {
    hideMessageModal();
    return;
  }
  showMessageModal(text, options);
}

function pushLocalizedStatus(messageKey, color = '#858585', options = {}) {
  const { titleKey, ...modalOptions } = options;
  localizedStatus = { messageKey, titleKey, color };
  const messages = t();
  const text = messages[messageKey] || '';
  setStatus(text, color, { keepLocalized: true });
  showMessageModal(text, {
    ...modalOptions,
    title: titleKey ? messages[titleKey] : modalOptions.title,
  });
  localizedStatus = { messageKey, titleKey, color };
}

function refreshLocalizedStatus() {
  if (!localizedStatus) return;
  const messages = t();
  const text = messages[localizedStatus.messageKey] || '';
  setStatus(text, localizedStatus.color, { keepLocalized: true });
  if (messageModal?.style.display === 'block' && messageModalText) {
    messageModalText.textContent = text;
    if (localizedStatus.titleKey && messageModalTitle) {
      messageModalTitle.textContent = messages[localizedStatus.titleKey] || '';
    }
  }
}

function pushProjectStatus(text, color = '#64748b', options = {}) {
  setProjectStatus(text, color);
  if (!text) return;
  showMessageModal(text, options);
}

function isAccelKey(event) {
  return event.ctrlKey || event.metaKey;
}

function selectionRange(view) {
  const main = view.state.selection.main;
  if (!main.empty) {
    return { from: main.from, to: main.to };
  }
  const line = view.state.doc.lineAt(main.head);
  const to = Math.min(line.to + 1, view.state.doc.length);
  return { from: line.from, to };
}

async function cutSelection() {
  const range = selectionRange(editorView);
  const text = editorView.state.sliceDoc(range.from, range.to);
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    showMessageModal(error.message || t().clipboardWriteFailed, {
      title: t().clipboardWriteFailedTitle,
      kind: 'error',
    });
    return;
  }
  editorView.dispatch({
    changes: { from: range.from, to: range.to, insert: '' },
    selection: { anchor: range.from },
  });
  editorView.focus();
}

function formatEditorCode() {
  const original = getCode(editorView);
  const formatted = formatTianyuanCode(original);
  if (formatted === original) {
    appendOutput(t().formatNoChanges);
    showMessageModal(t().formatNoChanges, {
      title: t().formatNoChangesTitle,
      kind: 'info',
      autoCloseMs: 1400,
    });
    return;
  }
  editorView.dispatch({
    changes: { from: 0, to: editorView.state.doc.length, insert: formatted },
  });
  appendOutput(t().formatApplied);
  showMessageModal(t().formatApplied, {
    title: t().formatAppliedTitle,
    kind: 'success',
    autoCloseMs: 1400,
  });
}

async function lintWorkspaceCode() {
  if (!ensureWorkspaceUser()) return;
  if (lintInFlight) return;
  lintInFlight = true;
  try {
    const body = await fetchJson('/api/workspace/lint', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: getCode(editorView) }),
    });
    const diagnostics = normalizeLintResponse(body);
    if (!diagnostics.length) {
      appendOutput(t().lintOk);
      showMessageModal(t().lintOk, {
        title: t().lintOkTitle,
        kind: 'success',
        autoCloseMs: 1600,
      });
      return;
    }
    diagnostics.forEach((item) => {
      const prefix = item.line && item.column ? `${item.line}:${item.column}` : t().lintUnknownLocation;
      appendError(`${prefix} ${item.message}`);
    });
    showMessageModal(
      formatMessage(t().lintFailedSummary, { count: diagnostics.length }),
      { title: t().lintFailedTitle, kind: 'error' },
    );
  } catch (error) {
    showMessageModal(error.message || t().lintRequestFailed, {
      title: t().lintFailedTitle,
      kind: 'error',
    });
  } finally {
    lintInFlight = false;
  }
}

function applyAuthState(user) {
  const previousUser = currentUser;
  if (previousUser && !user) {
    stopWorkspaceUsageTracking({ flush: true });
  }
  currentUser = user;
  if (!authPill || !authPillText || !authPillLink || !menuAccountCenter || !menuAuthAction) return;
  authPill.hidden = false;
  menuAuthAction.style.display = 'flex';
  if (user) {
    authPillText.textContent = user.username || user.email;
    authPillLink.textContent = t().authAccount;
    authPillLink.href = '/account';
    menuAccountCenter.style.display = 'flex';
    menuAuthAction.textContent = t().authLogout;
  } else {
    currentBilling = null;
    quotaClientActiveStartedAt = null;
    stopQuotaDisplayTicker();
    authPillText.textContent = t().authLoggedOut;
    authPillLink.textContent = t().authLogin;
    authPillLink.href = buildLoginUrl('/workspace');
    menuAccountCenter.style.display = 'none';
    menuAuthAction.textContent = t().authLogin;
  }
  renderQuotaPill();
}

setAuthLoadingState();

fetchPublicRollout()
  .then((body) => {
    if (menuHelpBilling) menuHelpBilling.hidden = !capabilityEnabled(body.capabilities, 'pricing_page_visible', false);
    if (menuHelpEducation) menuHelpEducation.hidden = !capabilityEnabled(body.capabilities, 'classroom_management', false);
    if (menuHelpTeams) menuHelpTeams.hidden = !capabilityEnabled(body.capabilities, 'team_collaboration', false);
  })
  .catch(() => {
    if (menuHelpBilling) menuHelpBilling.hidden = true;
    if (menuHelpEducation) menuHelpEducation.hidden = true;
    if (menuHelpTeams) menuHelpTeams.hidden = true;
  });

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) {
    throw new Error(getLocale() === 'en' ? 'Please sign in first' : '请先登录');
  }
  if (!res.ok) {
    throw new Error(body.detail || `${res.status} ${res.statusText}`);
  }
  return body;
}

function setWorkspaceAuthStatus(text, isError = false) {
  if (!workspaceAuthStatus) return;
  workspaceAuthStatus.textContent = text || '';
  workspaceAuthStatus.dataset.error = isError ? 'true' : 'false';
}

function showWorkspaceAuthModal({ focus = true } = {}) {
  if (!workspaceAuthModal) {
    window.location.href = buildLoginUrl('/workspace');
    return;
  }
  setWorkspaceAuthStatus('');
  workspaceAuthModal.style.display = 'block';
  if (focus) {
    window.setTimeout(() => {
      workspaceAuthIdentifierInput?.focus();
    }, 0);
  }
}

function hideWorkspaceAuthModal() {
  if (!workspaceAuthModal) return;
  workspaceAuthModal.style.display = 'none';
}

function ensureWorkspaceUser() {
  if (currentUser) return true;
  showWorkspaceAuthModal();
  return false;
}

function startWorkspaceConnection() {
  if (workspaceConnectionStarted) return;
  workspaceConnectionStarted = true;
  connect(() => {
    pushLocalizedStatus('connectedReady', '#858585', {
      titleKey: 'connectedReadyTitle',
      kind: 'success',
      autoCloseMs: 1200,
    });
  });
  pushLocalizedStatus('connecting', '#858585', {
    titleKey: 'connectingTitle',
    kind: 'progress',
    dismissible: false,
  });
}

function ensureWorkspaceUsageSessionId() {
  const existing = getSessionId() || sessionStorage.getItem('ty_session_id');
  if (existing) return existing;
  const generated = crypto.randomUUID();
  sessionStorage.setItem('ty_session_id', generated);
  return generated;
}

function isWorkspaceUsageActive() {
  return Boolean(currentUser && document.visibilityState === 'visible' && document.hasFocus());
}

function clearWorkspaceUsageTimer() {
  if (workspaceUsageTimer) {
    window.clearTimeout(workspaceUsageTimer);
    workspaceUsageTimer = null;
  }
}

async function sendWorkspaceUsageHeartbeat({ active = isWorkspaceUsageActive(), keepalive = false, force = false } = {}) {
  if (!currentUser) return;
  if (workspaceUsageInFlight && !force) return;
  workspaceUsageInFlight = true;
  try {
    const res = await fetch('/api/workspace/usage/heartbeat', {
      method: 'POST',
      credentials: 'include',
      keepalive,
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': localeHeaderValue(),
      },
      body: JSON.stringify({
        session_id: ensureWorkspaceUsageSessionId(),
        active,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.status === 401) {
      stopWorkspaceUsageTracking();
      applyAuthState(null);
      showWorkspaceAuthModal({ focus: false });
      return;
    }
    if (!res.ok) {
      throw new Error(body.detail || `${res.status} ${res.statusText}`);
    }
    if (body.billing) {
      currentBilling = body.billing;
      resetQuotaClientUsageBaseline();
      renderQuotaPill();
    }
  } catch (error) {
    if (!keepalive) {
      console.warn('[Workspace] 使用时长心跳失败:', error);
    }
  } finally {
    workspaceUsageInFlight = false;
  }
}

function scheduleWorkspaceUsageHeartbeat() {
  clearWorkspaceUsageTimer();
  if (!currentUser) return;
  workspaceUsageTimer = window.setTimeout(() => {
    void sendWorkspaceUsageHeartbeat().finally(scheduleWorkspaceUsageHeartbeat);
  }, WORKSPACE_USAGE_HEARTBEAT_MS);
}

function startWorkspaceUsageTracking() {
  if (!currentUser) return;
  resetQuotaClientUsageBaseline();
  void sendWorkspaceUsageHeartbeat();
  scheduleWorkspaceUsageHeartbeat();
}

function stopWorkspaceUsageTracking({ flush = false } = {}) {
  clearWorkspaceUsageTimer();
  if (flush && currentUser) {
    void sendWorkspaceUsageHeartbeat({ active: false, keepalive: true, force: true });
  }
}

function handleWorkspaceUsageActivityChange() {
  if (!currentUser) return;
  resetQuotaClientUsageBaseline();
  void sendWorkspaceUsageHeartbeat({ active: isWorkspaceUsageActive(), keepalive: true, force: true });
  scheduleWorkspaceUsageHeartbeat();
}

async function refreshWorkspaceQuota({ delayMs = 0 } = {}) {
  if (!currentUser || quotaLoadInFlight) {
    renderQuotaPill();
    return;
  }
  quotaLoadInFlight = true;
  renderQuotaPill({ loading: true });
  if (delayMs > 0) {
    await new Promise(resolve => window.setTimeout(resolve, delayMs));
  }
  try {
    currentBilling = await fetchJson('/api/billing/me');
    resetQuotaClientUsageBaseline();
  } catch (error) {
    console.warn('[Workspace] 额度信息加载失败:', error);
  } finally {
    quotaLoadInFlight = false;
    renderQuotaPill();
  }
}

function formatWorkspaceAuthError(body, fallbackText) {
  if (typeof body?.detail === 'string' && body.detail.trim()) {
    return body.detail;
  }
  if (Array.isArray(body?.detail) && body.detail.length > 0) {
    const firstMessage = body.detail[0]?.msg;
    if (typeof firstMessage === 'string' && firstMessage.trim()) {
      return firstMessage;
    }
  }
  return fallbackText;
}

function setWorkspaceAuthSubmitting(submitting) {
  workspaceAuthSubmitting = submitting;
  if (workspaceAuthSubmit) {
    workspaceAuthSubmit.disabled = submitting;
    workspaceAuthSubmit.textContent = submitting ? t().authModalSubmitting : t().authModalSubmit;
  }
  if (workspaceAuthIdentifierInput) workspaceAuthIdentifierInput.disabled = submitting;
  if (workspaceAuthPasswordInput) workspaceAuthPasswordInput.disabled = submitting;
}

async function submitWorkspaceAuth() {
  if (workspaceAuthSubmitting) return;
  const identifier = workspaceAuthIdentifierInput?.value?.trim() || '';
  const password = workspaceAuthPasswordInput?.value || '';
  if (!identifier || !password) {
    setWorkspaceAuthStatus(t().authModalRequired, true);
    return;
  }
  try {
    setWorkspaceAuthSubmitting(true);
    setWorkspaceAuthStatus('');
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': localeHeaderValue(),
      },
      credentials: 'include',
      body: JSON.stringify({ identifier, password }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(formatWorkspaceAuthError(body, t().authModalFailed));
    }
    applyAuthState(body.user);
    hideWorkspaceAuthModal();
    void refreshWorkspaceQuota();
    startWorkspaceConnection();
    startWorkspaceUsageTracking();
  } catch (error) {
    setWorkspaceAuthStatus(error.message || t().authModalFailed, true);
  } finally {
    setWorkspaceAuthSubmitting(false);
  }
}

function openProjectModal() {
  if (!projectModal) return;
  projectModal.style.display = 'block';
  setProjectStatus('');
  void loadProjects();
}

function openProjectModalForSave(message = t().saveProjectBefore) {
  openProjectModal();
  window.setTimeout(() => {
    projectNameInput?.focus();
    projectNameInput?.select();
  }, 0);
  pushProjectStatus(message, '#2563eb', {
    title: t().saveProjectBeforeTitle,
    kind: 'info',
  });
}

function openProjectModalForExport(message = t().exportProjectBefore) {
  openProjectModal();
  window.setTimeout(() => {
    const firstAction = projectListBody?.querySelector('[data-project-export], [data-project-open]');
    firstAction?.focus?.();
  }, 0);
  pushProjectStatus(message, '#2563eb', {
    title: t().exportProjectBeforeTitle,
    kind: 'info',
  });
}

function closeProjectModal() {
  if (!projectModal) return;
  projectModal.style.display = 'none';
}

function updateFeedbackMeta() {
  if (feedbackPageMeta) {
    feedbackPageMeta.textContent = formatMessage(t().feedbackPageMeta, { path: window.location.pathname || '/workspace' });
  }
  if (feedbackSessionMeta) {
    feedbackSessionMeta.textContent = formatMessage(t().feedbackSessionMeta, { sessionId: getSessionId() || '-' });
  }
}

function setFeedbackSubmittingState(submitting) {
  feedbackSubmitInFlight = submitting;
  if (feedbackSubmitBtn) {
    feedbackSubmitBtn.disabled = submitting;
    feedbackSubmitBtn.textContent = submitting ? t().feedbackSubmitting : t().feedbackSubmit;
  }
  if (feedbackFillCodeBtn) feedbackFillCodeBtn.disabled = submitting;
  if (feedbackClearCodeBtn) feedbackClearCodeBtn.disabled = submitting;
  if (feedbackTitleInput) feedbackTitleInput.disabled = submitting;
  if (feedbackDescriptionInput) feedbackDescriptionInput.disabled = submitting;
  if (feedbackCodeInput) feedbackCodeInput.disabled = submitting;
}

function openFeedbackModal() {
  if (!feedbackModal) return;
  updateFeedbackMeta();
  feedbackModal.style.display = 'block';
  feedbackTitleInput?.focus();
}

function closeFeedbackModal({ reset = false } = {}) {
  if (!feedbackModal) return;
  feedbackModal.style.display = 'none';
  if (reset) {
    if (feedbackTitleInput) feedbackTitleInput.value = '';
    if (feedbackDescriptionInput) feedbackDescriptionInput.value = '';
    if (feedbackCodeInput) feedbackCodeInput.value = '';
  }
}

async function submitFeedback() {
  if (feedbackSubmitInFlight) return;
  const title = feedbackTitleInput?.value?.trim() || '';
  const description = feedbackDescriptionInput?.value?.trim() || '';
  const codeContent = feedbackCodeInput?.value?.trim() || null;
  if (!title) {
    showMessageModal(t().feedbackTitleRequired, { title: t().feedbackSubmitFailedTitle, kind: 'error' });
    feedbackTitleInput?.focus();
    return;
  }
  if (!description) {
    showMessageModal(t().feedbackDescriptionRequired, { title: t().feedbackSubmitFailedTitle, kind: 'error' });
    feedbackDescriptionInput?.focus();
    return;
  }
  try {
    setFeedbackSubmittingState(true);
    const body = await fetchJson('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title,
        description,
        code_content: codeContent,
        page_path: window.location.pathname || '/workspace',
        workspace_session_id: getSessionId() || null,
      }),
    });
    closeFeedbackModal({ reset: true });
    showMessageModal(
      formatMessage(t().feedbackSubmitSuccess, { id: body.issue?.id || '-' }),
      { title: t().feedbackSubmitSuccessTitle, kind: 'success', autoCloseMs: 2400 },
    );
  } catch (error) {
    showMessageModal(error.message || t().feedbackSubmitFailed, {
      title: t().feedbackSubmitFailedTitle,
      kind: 'error',
    });
  } finally {
    setFeedbackSubmittingState(false);
  }
}

function setProjectSavingState(saving) {
  projectSaveInFlight = saving;
  if (projectSaveBtn) {
    projectSaveBtn.disabled = saving;
    projectSaveBtn.textContent = saving ? t().projectSaving : t().projectSaveButton;
  }
  if (btnCloudSave) {
    btnCloudSave.disabled = saving;
  }
  if (menuFileCloudSave) {
    menuFileCloudSave.style.pointerEvents = saving ? 'none' : '';
    menuFileCloudSave.style.opacity = saving ? '0.5' : '';
  }
}

async function loadProjectRevisions(projectId) {
  if (!projectId || !projectRevisionsBody) return;
  try {
    const body = await fetchJson(`/api/projects/${projectId}/revisions`);
    const revisions = body.revisions || [];
    projectRevisionsBody.innerHTML = revisions.length
      ? revisions.map((revision) => `
        <tr>
          <td>#${revision.revision_no}</td>
          <td>${revision.change_summary || '-'}</td>
          <td>${revision.created_at ? new Date(revision.created_at * 1000).toLocaleString('zh-CN', { hour12: false }) : '-'}</td>
        </tr>
      `).join('')
      : `<tr><td colspan="3" class="project-table-empty">${t().noRevisionHistory}</td></tr>`;
  } catch (error) {
    projectRevisionsBody.innerHTML = `<tr><td colspan="3" class="project-table-empty">${error.message || t().revisionLoadFailed}</td></tr>`;
    pushProjectStatus(error.message || t().revisionLoadFailed, '#c43d3d', { title: t().loadFailed, kind: 'error' });
  }
}

async function loadProjects() {
  if (!projectListBody) return;
  try {
    const body = await fetchJson('/api/projects');
    const projects = body.projects || [];
    projectListBody.innerHTML = projects.length
      ? projects.map((project) => `
        <tr>
          <td>${project.name}</td>
          <td>${project.visibility}</td>
          <td>${project.updated_at ? new Date(project.updated_at * 1000).toLocaleString('zh-CN', { hour12: false }) : '-'}</td>
          <td class="inline-actions">
            <button class="inline-action" data-project-open="${project.id}">${t().projectOpen}</button>
            <button class="inline-action" data-project-export="${project.id}">${t().projectExport}</button>
          </td>
        </tr>
      `).join('')
      : `<tr><td colspan="4" class="project-table-empty">${t().noProjects}</td></tr>`;
    document.querySelectorAll('[data-project-open]').forEach((button) => {
      button.addEventListener('click', async () => {
        await openProject(button.dataset.projectOpen);
      });
    });
    document.querySelectorAll('[data-project-export]').forEach((button) => {
      button.addEventListener('click', async () => {
        await exportProject(button.dataset.projectExport);
      });
    });
    if (currentProjectId) {
      await loadProjectRevisions(currentProjectId);
    }
  } catch (error) {
    projectListBody.innerHTML = `<tr><td colspan="4" class="project-table-empty">${error.message || t().projectLoadFailed}</td></tr>`;
    pushProjectStatus(error.message || t().projectLoadFailed, '#c43d3d', { title: t().loadFailed, kind: 'error' });
  }
}

async function openProject(projectId) {
  const body = await fetchJson(`/api/projects/${projectId}`);
  const file = (body.files || [])[0];
  currentProjectId = body.project.id;
  currentProjectName = body.project.name;
  projectNameInput.value = body.project.name || '';
  projectDescInput.value = body.project.description || '';
  projectVisibilitySelect.value = body.project.visibility || 'private';
  if (file) {
    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: file.content || '' }
    });
  }
  await loadProjectRevisions(projectId);
  pushProjectStatus(formatMessage(t().projectOpened, { name: body.project.name }), '#2563eb', {
    title: t().projectOpenedTitle,
    kind: 'success',
    autoCloseMs: 1600,
  });
  setStatus(formatMessage(t().projectOpened, { name: body.project.name }), '#2563eb');
  closeProjectModal();
}

async function saveProject() {
  if (projectSaveInFlight) {
    pushProjectStatus(t().projectBusy, '#2563eb', {
      title: t().savingTitle,
      kind: 'progress',
      autoCloseMs: 1200,
    });
    setStatus(t().projectBusy, '#2563eb');
    return;
  }
  if (!currentUser) {
    window.location.href = buildLoginUrl('/workspace');
    return;
  }
  const name = (projectNameInput?.value || currentProjectName || '').trim();
  if (!name) {
    openProjectModalForSave();
    setStatus(t().inputProjectName, '#c43d3d');
    return;
  }
  const payload = {
    name,
    description: projectDescInput?.value?.trim() || null,
    visibility: projectVisibilitySelect?.value || 'private',
    language: 'tianyuan',
    change_summary: t().workspaceSaveSummary,
    files: [
      {
        path: 'main.ty',
        content: getCode(editorView),
        file_type: 'script',
      },
    ],
  };
  try {
    setProjectSavingState(true);
    pushProjectStatus(t().savingProject, '#2563eb', {
      title: t().savingTitle,
      kind: 'progress',
      dismissible: false,
    });
    setStatus(t().savingProject, '#2563eb');
    let body;
    if (currentProjectId) {
      body = await fetchJson(`/api/projects/${currentProjectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } else {
      body = await fetchJson('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      currentProjectId = body.id;
      currentProjectName = body.name;
    }
    await loadProjects();
    await loadProjectRevisions(currentProjectId);
    pushProjectStatus(formatMessage(t().projectSaved, { name: currentProjectName || name }), '#2563eb', {
      title: t().saveSuccessTitle,
      kind: 'success',
      autoCloseMs: 1600,
    });
    setStatus(formatMessage(t().projectSaved, { name: currentProjectName || name }), '#2563eb');
  } catch (error) {
    pushProjectStatus(error.message || t().saveFailed, '#c43d3d', { title: t().saveFailedTitle, kind: 'error' });
    setStatus(error.message || t().saveFailed, '#c43d3d');
  } finally {
    setProjectSavingState(false);
  }
}

async function exportProject(projectId = currentProjectId) {
  if (!projectId) {
    openProjectModalForExport();
    setStatus(t().selectSavedProject, '#c43d3d');
    return;
  }
  try {
    const body = await fetchJson(`/api/projects/${projectId}/export`);
    const blob = new Blob([JSON.stringify(body, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${body.project.name || 'project'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    pushProjectStatus(t().projectExported, '#2563eb', {
      title: t().exportSuccessTitle,
      kind: 'success',
      autoCloseMs: 1600,
    });
    setStatus(t().projectExported, '#2563eb');
  } catch (error) {
    pushProjectStatus(error.message || t().exportFailed, '#c43d3d', { title: t().exportFailedTitle, kind: 'error' });
    setStatus(error.message || t().exportFailed, '#c43d3d');
  }
}

// ws.js 执行完毕后刷新变量面板和补全缓存
document.addEventListener('ty:done', () => {
  setTimeout(() => {
    refreshVars();
    refreshVarCompletions();
  }, 100);
});

// output.js 通过自定义事件通知运行状态变化
document.addEventListener('ty:running', (e) => {
  const running = e.detail.running;
  btnRun.disabled = running;

  if (stopButtonTimer) {
    clearTimeout(stopButtonTimer);
    stopButtonTimer = null;
  }

  if (running) {
    btnStop.disabled = true;
    // 短任务不暴露“中断”，避免界面无意义闪烁。
    stopButtonTimer = setTimeout(() => {
      btnStop.disabled = false;
      stopButtonTimer = null;
    }, INTERRUPT_BUTTON_DELAY_MS);
  } else {
    btnStop.disabled = true;
  }

  setStatus(running ? t().running : t().ready, running ? '#cca700' : '#858585');
});

// ── 编辑器挂载 ────────────────────────────────────────────────────────────────
function runCode() {
  if (!ensureWorkspaceUser()) return;
  const code = getCode(editorView);
  appendCode(code);
  execute(code);
}

const editorView = createEditor(
  document.getElementById('editor-container'),
  runCode
);

function installWorkspaceTestHooks() {
  if (typeof window === 'undefined') return;
  window.__TY_WORKSPACE__ = {
    setCode(code) {
      editorView.dispatch({
        changes: { from: 0, to: editorView.state.doc.length, insert: code },
      });
    },
    getCode() {
      return getCode(editorView);
    },
    run() {
      runCode();
    },
    getStatus() {
      return statusEl?.textContent || '';
    },
    isConnected() {
      return isConnected();
    },
    getSessionId() {
      return getSessionId();
    },
    getOutputText() {
      return document.getElementById('output-scroll')?.innerText || '';
    },
    getVariableRows() {
      return Array.from(document.querySelectorAll('#var-tbody tr')).map((row) =>
        Array.from(row.querySelectorAll('td')).map((cell) => cell.textContent?.trim() || '')
      );
    },
    async getFigureSummaries() {
      const renderer = await import('./renderer.js');
      return renderer.getFigureSummaries();
    },
  };
}

installWorkspaceTestHooks();

// ── 工具栏与菜单栏事件 ─────────────────────────────────────────────────────────

// --- 通用执行逻辑 ---
btnRun.addEventListener('click', runCode);
btnStop.addEventListener('click', () => interrupt());

const clearWorkspaceFn = () => {
  if (!ensureWorkspaceUser()) return;
  clearWorkspace();
  document.getElementById('output-scroll').innerHTML = '';
  refreshVars();
};
document.getElementById('btn-clear').addEventListener('click', clearWorkspaceFn);

// --- 文件 (File) ---
const fileInput = document.createElement('input');
fileInput.type = 'file';
fileInput.accept = '.ty,.txt';
fileInput.style.display = 'none';
document.body.appendChild(fileInput);

fileInput.onchange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: e.target.result }
    });
  };
  reader.readAsText(file);
};

document.getElementById('menu-file-new').addEventListener('click', async () => {
  if (await showConfirmDialog(t().newScriptConfirm, { title: t().menuFileNew })) {
    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: t().newScriptTemplate }
    });
  }
});

document.getElementById('menu-file-open').addEventListener('click', () => fileInput.click());

const saveFile = (filename = 'script.ty') => {
  const code = getCode(editorView);
  const blob = new Blob([code], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

document.getElementById('menu-file-save').addEventListener('click', () => saveFile());
document.getElementById('menu-file-saveas').addEventListener('click', async () => {
  const name = await showPromptDialog(t().promptFilename, {
    title: t().menuFileSaveAs,
    defaultValue: 'script.ty',
  });
  if (name) saveFile(name);
});
menuFileCloudSave?.addEventListener('click', saveProject);
document.getElementById('menu-file-projects').addEventListener('click', openProjectModal);
document.getElementById('menu-file-export').addEventListener('click', () => exportProject());

// --- 编辑 (Edit) ---
document.getElementById('menu-edit-undo').addEventListener('click', () => {
  editorView.focus();
  undo(editorView);
});
document.getElementById('menu-edit-redo').addEventListener('click', () => {
  editorView.focus();
  redo(editorView);
});
document.getElementById('menu-edit-cut').addEventListener('click', () => {
  void cutSelection();
});
document.getElementById('menu-edit-copy').addEventListener('click', () => {
  const selection = editorView.state.sliceDoc(
    editorView.state.selection.main.from,
    editorView.state.selection.main.to
  );
  const text = selection || getCode(editorView);
  navigator.clipboard.writeText(text);
});
document.getElementById('menu-edit-paste').addEventListener('click', async () => {
  const text = await navigator.clipboard.readText();
  editorView.dispatch(editorView.state.replaceSelection(text));
});

// --- 视图 (View) ---
const rightPanel = document.getElementById('right-panel');
const outputPanel = document.getElementById('output-panel');

document.getElementById('menu-view-explorer').addEventListener('click', () => {
  rightPanel.style.display = rightPanel.style.display === 'none' ? 'flex' : 'none';
});

document.getElementById('menu-view-output').addEventListener('click', () => {
  outputPanel.style.display = outputPanel.style.display === 'none' ? 'flex' : 'none';
});

document.getElementById('menu-view-reset').addEventListener('click', () => {
  window.location.reload();
});

// --- 运行 (Run) ---
document.getElementById('menu-run-all').addEventListener('click', runCode);
document.getElementById('menu-run-stop').addEventListener('click', () => interrupt());
document.getElementById('menu-run-clear').addEventListener('click', clearWorkspaceFn);
document.getElementById('menu-tools-format').addEventListener('click', formatEditorCode);
document.getElementById('menu-tools-lint').addEventListener('click', () => {
  void lintWorkspaceCode();
});

document.getElementById('btn-refresh-vars').addEventListener('click', () => {
  refreshVars();
});
btnCloudSave?.addEventListener('click', saveProject);
document.getElementById('btn-projects').addEventListener('click', openProjectModal);
document.getElementById('btn-export-project').addEventListener('click', () => exportProject());
btnFeedback?.addEventListener('click', openFeedbackModal);

projectSaveBtn?.addEventListener('click', saveProject);
projectClose?.addEventListener('click', closeProjectModal);
feedbackClose?.addEventListener('click', () => closeFeedbackModal());
feedbackSubmitBtn?.addEventListener('click', submitFeedback);
feedbackFillCodeBtn?.addEventListener('click', () => {
  if (feedbackCodeInput) feedbackCodeInput.value = getCode(editorView);
});
feedbackClearCodeBtn?.addEventListener('click', () => {
  if (feedbackCodeInput) feedbackCodeInput.value = '';
});

menuAccountCenter?.addEventListener('click', () => {
  window.location.href = '/account';
});

menuAuthAction?.addEventListener('click', async () => {
  if (currentUser) {
    await logout();
    window.location.reload();
    return;
  }
  showWorkspaceAuthModal();
});

workspaceAuthForm?.addEventListener('submit', (event) => {
  event.preventDefault();
  void submitWorkspaceAuth();
});
workspaceAuthClose?.addEventListener('click', hideWorkspaceAuthModal);
workspaceAuthLater?.addEventListener('click', hideWorkspaceAuthModal);

document.addEventListener('keydown', (event) => {
  const target = event.target;
  const isTextInput = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
  const isEditorContent = target instanceof HTMLElement && target.closest('.cm-editor');
  if (isAccelKey(event) && event.key.toLowerCase() === 'x' && (isEditorContent || !isTextInput)) {
    event.preventDefault();
    void cutSelection();
    return;
  }
  if (isAccelKey(event) && event.shiftKey && event.key.toLowerCase() === 'f' && (isEditorContent || !isTextInput)) {
    event.preventDefault();
    formatEditorCode();
    return;
  }
  if (event.key === 'F8' && (isEditorContent || !isTextInput)) {
    event.preventDefault();
    void lintWorkspaceCode();
  }
});

document.addEventListener('visibilitychange', handleWorkspaceUsageActivityChange);
window.addEventListener('focus', handleWorkspaceUsageActivityChange);
window.addEventListener('blur', handleWorkspaceUsageActivityChange);
window.addEventListener('pagehide', () => stopWorkspaceUsageTracking({ flush: true }));
window.addEventListener('beforeunload', () => stopWorkspaceUsageTracking({ flush: true }));

const bootstrapAuthState = () => {
  fetchCurrentUser()
    .then((user) => {
      if (!user) {
        applyAuthState(null);
        showWorkspaceAuthModal();
        return;
      }
      applyAuthState(user);
      void refreshWorkspaceQuota();
      startWorkspaceConnection();
      startWorkspaceUsageTracking();
    })
    .catch((error) => {
      console.warn('[Workspace] 登录态校验失败:', error);
      applyAuthState(null);
      showWorkspaceAuthModal();
      setWorkspaceAuthStatus(t().authModalPreloadFailed, true);
    });
};

bootstrapAuthState();

// ── 弹窗控制 (About Modal) ──────────────────────────────────────────────────
const aboutModal = document.getElementById('about-modal');
const btnAbout   = document.getElementById('menu-help-about');
const btnClose   = document.getElementById('about-close');

btnAbout.addEventListener('click', () => {
  aboutModal.style.display = 'block';
});

btnClose.addEventListener('click', () => {
  aboutModal.style.display = 'none';
});
menuHelpFeedback?.addEventListener('click', openFeedbackModal);
messageModalClose?.addEventListener('click', hideMessageModal);
messageModalConfirm?.addEventListener('click', hideMessageModal);

// 点击弹窗外部关闭
window.addEventListener('click', (event) => {
  if (event.target === aboutModal) {
    aboutModal.style.display = 'none';
  }
  if (event.target === projectModal) {
    closeProjectModal();
  }
  if (event.target === feedbackModal) {
    closeFeedbackModal();
  }
  if (event.target === messageModal) {
    if (messageModalDismissible) {
      hideMessageModal();
    }
  }
  if (event.target === workspaceAuthModal) {
    hideWorkspaceAuthModal();
  }
});

// ── REPL 输入行 ───────────────────────────────────────────────────────────────
const replInput = document.getElementById('repl-input');
const replHistory = [];
let replHistoryIdx = -1;

replInput.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    if (!ensureWorkspaceUser()) return;
    const code = replInput.value;
    if (!code.trim()) return;
    replHistory.unshift(code);
    replHistoryIdx = -1;
    replInput.value = '';
    appendCode(code);
    execute(code);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (replHistoryIdx < replHistory.length - 1) {
      replHistoryIdx++;
      replInput.value = replHistory[replHistoryIdx];
    }
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (replHistoryIdx > 0) {
      replHistoryIdx--;
      replInput.value = replHistory[replHistoryIdx];
    } else {
      replHistoryIdx = -1;
      replInput.value = '';
    }
  }
});

// 执行中禁用输入，完成后恢复焦点
document.addEventListener('ty:running', (e) => {
  replInput.disabled = e.detail.running;
  if (!e.detail.running) replInput.focus();
});

// ── 分割条初始化 ──────────────────────────────────────────────────────────────
initResizers();

function applyLocalizedText() {
  document.title = t().title;
  document.getElementById('workspace-brand-text')?.replaceChildren(document.createTextNode(t().brandName || '天元计算'));
  setMenuLabel('menu-label-file', t().fileMenu);
  setMenuLabel('menu-label-edit', t().editMenu);
  setMenuLabel('menu-label-view', t().viewMenu);
  setMenuLabel('menu-label-run', t().runMenu);
  setMenuLabel('menu-label-tools', t().toolsMenu);
  setMenuLabel('menu-label-help', t().helpMenu);
  document.getElementById('menu-file-new').textContent = t().menuFileNew;
  document.getElementById('menu-file-open').textContent = t().menuFileOpen;
  document.getElementById('menu-file-save').textContent = t().menuFileSave;
  document.getElementById('menu-file-saveas').textContent = t().menuFileSaveAs;
  menuFileCloudSave.textContent = t().menuFileCloudSave;
  document.getElementById('menu-file-projects').textContent = t().menuFileProjects;
  document.getElementById('menu-file-export').textContent = t().menuFileExport;
  document.getElementById('menu-edit-undo').textContent = t().menuEditUndo;
  document.getElementById('menu-edit-redo').textContent = t().menuEditRedo;
  document.getElementById('menu-edit-cut').textContent = t().menuEditCut;
  document.getElementById('menu-edit-copy').textContent = t().menuEditCopy;
  document.getElementById('menu-edit-paste').textContent = t().menuEditPaste;
  document.getElementById('menu-view-explorer').textContent = t().menuViewExplorer;
  document.getElementById('menu-view-output').textContent = t().menuViewOutput;
  document.getElementById('menu-view-reset').textContent = t().menuViewReset;
  document.getElementById('menu-run-all').textContent = t().menuRunAll;
  document.getElementById('menu-run-stop').textContent = t().menuRunStop;
  document.getElementById('menu-run-clear').textContent = t().menuRunClear;
  document.getElementById('menu-tools-format').textContent = t().menuToolsFormat;
  document.getElementById('menu-tools-lint').textContent = t().menuToolsLint;
  document.getElementById('menu-help-billing').textContent = t().menuHelpBilling;
  document.getElementById('menu-help-education').textContent = t().menuHelpEducation;
  document.getElementById('menu-help-teams').textContent = t().menuHelpTeams;
  document.getElementById('menu-account-center').textContent = t().menuAccountCenter;
  document.getElementById('menu-help-feedback').textContent = t().menuHelpFeedback;
  document.getElementById('menu-help-about').textContent = t().menuHelpAbout;
  btnRun.textContent = t().btnRun;
  btnStop.textContent = t().btnStop;
  document.getElementById('btn-clear').textContent = t().btnClear;
  btnCloudSave.textContent = t().btnCloudSave;
  document.getElementById('btn-projects').textContent = t().btnProjects;
  document.getElementById('btn-export-project').textContent = t().btnExportProject;
  document.getElementById('btn-docs').textContent = t().btnDocs;
  btnFeedback.textContent = t().btnFeedback;
  replInput.placeholder = t().workspacePlaceholder;
  projectNameInput.placeholder = t().projectNamePlaceholder;
  projectDescInput.placeholder = t().projectDescPlaceholder;
  if (feedbackModalTitle) feedbackModalTitle.textContent = t().feedbackModalTitle;
  if (feedbackTitleLabel) feedbackTitleLabel.textContent = t().feedbackTitleLabel;
  if (feedbackDescriptionLabel) feedbackDescriptionLabel.textContent = t().feedbackDescriptionLabel;
  if (feedbackCodeLabel) feedbackCodeLabel.textContent = t().feedbackCodeLabel;
  if (feedbackTitleInput) feedbackTitleInput.placeholder = t().feedbackTitlePlaceholder;
  if (feedbackDescriptionInput) feedbackDescriptionInput.placeholder = t().feedbackDescriptionPlaceholder;
  if (feedbackCodeInput) feedbackCodeInput.placeholder = t().feedbackCodePlaceholder;
  if (feedbackFillCodeBtn) feedbackFillCodeBtn.textContent = t().feedbackFillCode;
  if (feedbackClearCodeBtn) feedbackClearCodeBtn.textContent = t().feedbackClearCode;
  if (workspaceAuthTitle) workspaceAuthTitle.textContent = t().authModalTitle;
  if (workspaceAuthSubtitle) workspaceAuthSubtitle.textContent = t().authModalSubtitle;
  if (workspaceAuthIdentifierLabel) workspaceAuthIdentifierLabel.textContent = t().authModalIdentifierLabel;
  if (workspaceAuthPasswordLabel) workspaceAuthPasswordLabel.textContent = t().authModalPasswordLabel;
  if (workspaceAuthIdentifierInput) workspaceAuthIdentifierInput.placeholder = t().authModalIdentifierPlaceholder;
  if (workspaceAuthPasswordInput) workspaceAuthPasswordInput.placeholder = t().authModalPasswordPlaceholder;
  if (workspaceAuthSignup) workspaceAuthSignup.textContent = t().authModalSignup;
  if (workspaceAuthForgot) workspaceAuthForgot.textContent = t().authModalForgot;
  if (workspaceAuthLater) workspaceAuthLater.textContent = t().authModalLater;
  setWorkspaceAuthSubmitting(workspaceAuthSubmitting);
  updateFeedbackMeta();
  const projectModalHeaders = projectModal?.querySelectorAll('.project-modal-block h3') || [];
  if (projectModalHeaders[0]) projectModalHeaders[0].textContent = t().projectBlockSave;
  if (projectModalHeaders[1]) projectModalHeaders[1].textContent = t().projectBlockHistory;
  if (projectModalHeaders[2]) projectModalHeaders[2].textContent = t().projectBlockProjects;
  if (messageModalTitle?.textContent === getMessages('pages/workspace', 'zh').systemMessage || messageModalTitle?.textContent === getMessages('pages/workspace', 'en').systemMessage) {
    messageModalTitle.textContent = t().systemMessage;
  }
  if (messageModalConfirm) {
    messageModalConfirm.textContent = t().understood;
  }
  refreshLocalizedStatus();
  setFeedbackSubmittingState(feedbackSubmitInFlight);
  const aboutHeader = aboutModal?.querySelector('.modal-header');
  if (aboutHeader) {
    aboutHeader.childNodes[2].nodeValue = ` ${t().aboutTitle}`;
  }
  const aboutTitle = aboutModal?.querySelector('.about-title');
  const aboutDesc = aboutModal?.querySelector('.modal-body p');
  const aboutLinks = aboutModal?.querySelectorAll('.about-links a');
  const aboutFooter = aboutModal?.querySelector('.modal-footer');
  if (aboutTitle) aboutTitle.textContent = t().aboutProduct;
  if (aboutDesc) aboutDesc.textContent = t().aboutDesc;
  if (aboutLinks?.[0]) aboutLinks[0].textContent = t().aboutSite;
  if (aboutLinks?.[1]) aboutLinks[1].textContent = t().aboutDocs;
  if (aboutFooter) aboutFooter.textContent = t().aboutFooter;
  if (currentUser) {
    applyAuthState(currentUser);
  } else {
    setAuthLoadingState();
  }
  renderQuotaPill();
  setProjectSavingState(projectSaveInFlight);
}

applyLocalizedText();
onLocaleChange(applyLocalizedText);
